import { Fragment, type ReactNode } from "react";
import { RichText } from "@/components/lesson/RichText";

/**
 * Renders the TaskText subset used by Performance Tasks (see shared/performanceTasks.ts):
 * "# " headings, paragraphs separated by blank lines (single line breaks kept, for poems),
 * "- " bullets, "1. " numbered lines, "| a | b |" tables (first row = header), "> " callouts,
 * and the RichText inline set (**bold**, *italic*, `code`, $KaTeX$).
 */
export function TaskText({ text, className = "" }: { text: string; className?: string }) {
  if (!text) return null;
  const lines = text.replace(/\r/g, "").split("\n");
  const nodes: ReactNode[] = [];
  let i = 0;
  let key = 0;
  const inline = (s: string) => <RichText inline text={s} />;

  while (i < lines.length) {
    const line = lines[i];
    const t = line.trim();
    if (!t) { i++; continue; }
    if (/^#{1,3}\s+/.test(t)) {
      const level = t.match(/^(#{1,3})/)![1].length;
      const body = t.replace(/^#{1,3}\s+/, "");
      nodes.push(level === 1
        ? <h3 key={key++} className="text-lg font-bold mt-3 mb-1">{inline(body)}</h3>
        : <h4 key={key++} className="text-base font-semibold mt-2 mb-1">{inline(body)}</h4>);
      i++; continue;
    }
    if (t.startsWith("|")) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        const cells = lines[i].trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map(c => c.trim());
        if (!cells.every(c => /^:?-{2,}:?$/.test(c))) rows.push(cells);
        i++;
      }
      const [head, ...body] = rows;
      nodes.push(
        <div key={key++} className="overflow-x-auto my-2">
          <table className="text-sm border border-border rounded-lg overflow-hidden min-w-[280px]">
            {head && <thead className="bg-muted/60"><tr>{head.map((c, ci) => <th key={ci} className="px-3 py-1.5 text-left font-semibold border-b border-border">{inline(c)}</th>)}</tr></thead>}
            <tbody>{body.map((r, ri) => <tr key={ri} className={ri % 2 ? "bg-muted/30" : ""}>{r.map((c, ci) => <td key={ci} className="px-3 py-1.5 border-b border-border/60 align-top">{inline(c)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      continue;
    }
    if (/^[-•]\s+/.test(t)) {
      const items: string[] = [];
      while (i < lines.length && /^[-•]\s+/.test(lines[i].trim())) { items.push(lines[i].trim().replace(/^[-•]\s+/, "")); i++; }
      nodes.push(<ul key={key++} className="list-disc pl-6 space-y-1 my-2">{items.map((b, bi) => <li key={bi}>{inline(b)}</li>)}</ul>);
      continue;
    }
    if (/^\d+[.)]\s+/.test(t)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+[.)]\s+/.test(lines[i].trim())) { items.push(lines[i].trim().replace(/^\d+[.)]\s+/, "")); i++; }
      nodes.push(<ol key={key++} className="list-decimal pl-6 space-y-1 my-2">{items.map((b, bi) => <li key={bi}>{inline(b)}</li>)}</ol>);
      continue;
    }
    if (t.startsWith(">")) {
      const items: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) { items.push(lines[i].trim().replace(/^>\s?/, "")); i++; }
      nodes.push(<div key={key++} className="my-2 rounded-lg border-l-4 border-primary bg-primary/5 px-3 py-2 text-sm">{items.map((b, bi) => <Fragment key={bi}>{bi > 0 && <br />}{inline(b)}</Fragment>)}</div>);
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,3}\s|\||[-•]\s|\d+[.)]\s|>)/.test(lines[i].trim())) { para.push(lines[i].trim()); i++; }
    nodes.push(<p key={key++} className="my-2 leading-relaxed">{para.map((p, pi) => <Fragment key={pi}>{pi > 0 && <br />}{inline(p)}</Fragment>)}</p>);
  }
  return <div className={`task-text ${className}`}>{nodes}</div>;
}
